import { forgotPasswordTempEmail } from "@/api/auth/auth.mutations";
import type { ResetLinkErrorDetails } from "@/api/auth/auth.mutations.types";
import { Button } from "@/components/ui/Button/Button";
import { Input } from "@/components/ui/Input/Input";
import type { ApiError } from "@/types/common.types";
import { isAuthenticated } from "@/utils/authHelpers";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Card, Text, Title } from "@mantine/core";
import { useMutation } from "@tanstack/react-query";
import { Link, createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import z from "zod";
import styles from "./forgot-password.module.scss";
import { handleApiSuccess, showErrorAlert } from "@/utils/responseHandler";

const RESET_LINK_COOLDOWN_MS = 2 * 60 * 1000;

export const Route = createFileRoute("/_unauthenticated/forgot-password/")({
  beforeLoad: async () => {
    // If already authenticated, redirect to service providers
    if (isAuthenticated()) {
      throw redirect({ to: "/" });
    }
  },
  component: ForgotPasswordRouteComponent,
});

const forgotPasswordFormSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

// Infer TypeScript type from Zod schema
type TForgotPasswordFormData = z.infer<typeof forgotPasswordFormSchema>;

function ForgotPasswordRouteComponent() {
  const { navigate } = useRouter();

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<TForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordFormSchema),
  });

  const { mutate: forgotPasswordTempEmailMutation, isPending: isForgotPasswordTempEmailPending } =
    useMutation({
      mutationFn: forgotPasswordTempEmail,
      onSuccess: () => {
        reset();
        handleApiSuccess("", "Check your email for the magic link to complete the password reset");
        navigate({ to: "/login" });
      },
      onError: (error: unknown) => {
        const axiosError = error as ApiError;
        const errorCode = axiosError?.response?.data?.error?.code;
        const errorMessage = axiosError?.response?.data?.error?.message;
        if (errorCode === "USER_NOT_FOUND") {
          setError("email", {
            message: errorMessage ?? "No account found with this email address",
          });
        } else if (errorCode === "RESET_LINK_ALREADY_SENT") {
          const details = axiosError?.response?.data?.error?.details as unknown as
            | ResetLinkErrorDetails
            | undefined;
          const blockedUntil = details?.errors?.[0]?.blockedUntil;
          navigate({
            to: "/reset-link-expiry",
            search: {
              state: "rate-limited",
              until: blockedUntil ?? new Date(Date.now() + RESET_LINK_COOLDOWN_MS).toISOString(),
            },
          });
        } else {
          showErrorAlert(errorMessage ?? "An error occurred");
        }
      },
    });

  const onSubmit = (data: TForgotPasswordFormData) => {
    forgotPasswordTempEmailMutation(data);
  };

  const handleBackToLogin = () => {
    navigate({ to: "/login" });
  };

  return (
    <Box className={styles.forgot_password_container}>
      <Title className={styles.forgot_password_header}>Forgot Password?</Title>

      <Text className={styles.success_message}>
        You will receive an email with a temporary password.
      </Text>
      <Text className={styles.success_submessage}>Use it to log in and reset your password.</Text>
      <Card className={styles.forgot_password_body} radius={8}>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          className={styles.forgot_password_form}
        >
          <Input
            label="Email address"
            placeholder="youremail@here.com"
            type="email"
            required
            register={register("email")}
            size="large"
            error={errors.email?.message}
            dataTestId="email"
            width={"100%"}
          />

          <Button
            data-testid="reset-btn"
            variant="primary"
            loading={isForgotPasswordTempEmailPending}
            size="large"
            fullWidth
            onClick={handleSubmit(onSubmit)}
          >
            Request Password Reset Link
          </Button>

          <Link
            data-testid="back-to-login-link"
            to="/login"
            className={styles.back_to_login_link}
            onClick={handleBackToLogin}
          >
            Return to login
          </Link>
        </Box>
      </Card>
    </Box>
  );
}
